import { useEffect, useState, type KeyboardEvent } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Stack,
  Typography,
} from "@mui/material";

import type { HospitalMhlAutoRouteConfig } from "@thalamos/aspire-common";
import {
  baseTemplates,
  formTemplateHasHospitalField,
  getFullFormName,
  isValidEmail,
} from "@thalamos/aspire-common";

import { api } from "../api.js";
import { Button, Typeahead } from "~/components/design-system/index.js";

/** Organisation-level MHL auto-route config (includes form template selection) */
export type OrgMhlAutoRouteConfig = {
  enabledFormTemplates: string[];
  teams: { teamId: string }[];
};

/** Hospital-level MHL auto-route config (applies to all forms, no template selection) */
type HospitalMhlConfig = HospitalMhlAutoRouteConfig;

/** Combined type for the editor - supports both modes */
export type MhlAutoRouteConfig = OrgMhlAutoRouteConfig | HospitalMhlConfig;

export type MhlAutoRouteConfigEditorProps = {
  /** Current configuration value */
  config: MhlAutoRouteConfig | null | undefined;
  /** Callback when configuration changes */
  onChange: (config: MhlAutoRouteConfig) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /**
   * Whether this is hospital-level config (shows guest emails, hides form templates).
   * When true, the config applies to ALL form templates where the hospital is present.
   */
  showGuestEmails?: boolean;
};

/**
 * Reusable editor component for MHL auto-route configuration.
 * Used for both organisation-level and hospital-level configuration.
 */
export function MhlAutoRouteConfigEditor({
  config,
  onChange,
  disabled = false,
  showGuestEmails = false,
}: MhlAutoRouteConfigEditorProps) {
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [searchedTeams, setSearchedTeams] = useState<
    { id: string; name: string; type: string }[]
  >([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeamDetails, setSelectedTeamDetails] = useState<
    Map<string, { name: string; type: string }>
  >(new Map());
  const [formTemplateSearchQuery, setFormTemplateSearchQuery] = useState("");
  const [guestEmailInput, setGuestEmailInput] = useState("");
  const [guestEmailError, setGuestEmailError] = useState<string | null>(null);

  const selectedTeamIds = config?.teams?.map((t) => t.teamId) || [];
  // For org-level config (when !showGuestEmails), use enabledFormTemplates
  // For hospital-level config (when showGuestEmails), this is not used
  const selectedFormTemplateIds =
    !showGuestEmails && config && "enabledFormTemplates" in config
      ? config.enabledFormTemplates
      : [];
  // Guest emails only exist on hospital-level config
  const guestEmails =
    showGuestEmails && config && "guestEmails" in config
      ? config.guestEmails
      : [];

  // Fetch teams when search query changes
  useEffect(() => {
    const fetchTeams = async () => {
      if (!teamSearchQuery || teamSearchQuery.length < 2) {
        setSearchedTeams([]);
        return;
      }
      setLoadingTeams(true);
      try {
        const result = await api.teams.search(teamSearchQuery, 20, 0);
        const teams = result.data.results
          .filter((team) => team.status === "active")
          .map((team) => ({
            id: team.id,
            name: team.name,
            type: team.type,
          }));
        setSearchedTeams(teams);
      } catch {
        setSearchedTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };
    const debounced = setTimeout(() => {
      void fetchTeams();
    }, 300);
    return () => {
      clearTimeout(debounced);
    };
  }, [teamSearchQuery]);

  // Fetch details for already-selected teams
  useEffect(() => {
    const fetchSelectedTeamDetails = async () => {
      const missingTeamIds = selectedTeamIds.filter(
        (id) => !selectedTeamDetails.has(id),
      );
      if (missingTeamIds.length === 0) return;

      for (const teamId of missingTeamIds) {
        try {
          const result = await api.teams.get(teamId);
          const teamData = result.data;
          if (teamData) {
            setSelectedTeamDetails((prev) => {
              const next = new Map(prev);
              next.set(teamId, {
                name: teamData.name,
                type: teamData.type,
              });
              return next;
            });
          }
        } catch {
          // Team may have been deleted
        }
      }
    };
    void fetchSelectedTeamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedTeamIds)]);

  const handleAddTeam = (teamId: string) => {
    if (disabled || selectedTeamIds.includes(teamId)) return;
    const team = searchedTeams.find((t) => t.id === teamId);
    if (team) {
      setSelectedTeamDetails((prev) => {
        const next = new Map(prev);
        next.set(teamId, { name: team.name, type: team.type });
        return next;
      });
    }
    if (showGuestEmails) {
      // Hospital-level config - no enabledFormTemplates
      onChange({
        teams: [...(config?.teams || []), { teamId }],
        guestEmails,
      });
    } else {
      // Org-level config - include enabledFormTemplates
      onChange({
        enabledFormTemplates: selectedFormTemplateIds,
        teams: [...(config?.teams || []), { teamId }],
      });
    }
  };

  const handleRemoveTeam = (teamId: string) => {
    if (disabled) return;
    if (showGuestEmails) {
      // Hospital-level config - no enabledFormTemplates
      onChange({
        teams: (config?.teams || []).filter((t) => t.teamId !== teamId),
        guestEmails,
      });
    } else {
      // Org-level config - include enabledFormTemplates
      onChange({
        enabledFormTemplates: selectedFormTemplateIds,
        teams: (config?.teams || []).filter((t) => t.teamId !== teamId),
      });
    }
  };

  // Get form template options from baseTemplates
  const formTemplateOptions = baseTemplates.map((template) => ({
    id: template.id,
    label: getFullFormName(template),
  }));

  // Filter out already selected templates for typeahead options
  const availableFormTemplates = formTemplateOptions.filter(
    (t) => !selectedFormTemplateIds.includes(t.id),
  );

  const handleAddFormTemplate = (templateId: string) => {
    if (disabled || selectedFormTemplateIds.includes(templateId)) return;
    // Form templates only used in org-level config
    onChange({
      enabledFormTemplates: [...selectedFormTemplateIds, templateId],
      teams: config?.teams || [],
    });
  };

  const handleRemoveFormTemplate = (templateId: string) => {
    if (disabled) return;
    // Form templates only used in org-level config
    onChange({
      enabledFormTemplates: selectedFormTemplateIds.filter(
        (id) => id !== templateId,
      ),
      teams: config?.teams || [],
    });
  };

  const handleSelectAllFormTemplates = () => {
    if (disabled) return;
    // Form templates only used in org-level config
    onChange({
      enabledFormTemplates: formTemplateOptions.map((t) => t.id),
      teams: config?.teams || [],
    });
  };

  const handleSelectFormsWithoutHospital = () => {
    if (disabled) return;
    // Use the authoritative source from aspire-common to determine which forms don't have hospital fields
    const withoutHospital = formTemplateOptions
      .filter((t) => !formTemplateHasHospitalField(t.id))
      .map((t) => t.id);
    // Form templates only used in org-level config
    onChange({
      enabledFormTemplates: withoutHospital,
      teams: config?.teams || [],
    });
  };

  const handleClearFormTemplates = () => {
    if (disabled) return;
    // Form templates only used in org-level config
    onChange({
      enabledFormTemplates: [],
      teams: config?.teams || [],
    });
  };

  const handleAddGuestEmail = () => {
    const email = guestEmailInput.trim().toLowerCase();
    if (!email) return;

    if (!isValidEmail(email)) {
      setGuestEmailError("Please enter a valid email address");
      return;
    }

    if (guestEmails.includes(email)) {
      setGuestEmailError("This email has already been added");
      return;
    }

    setGuestEmailError(null);
    setGuestEmailInput("");
    // Guest emails only used in hospital-level config
    onChange({
      teams: config?.teams || [],
      guestEmails: [...guestEmails, email],
    });
  };

  const handleRemoveGuestEmail = (email: string) => {
    if (disabled) return;
    // Guest emails only used in hospital-level config
    onChange({
      teams: config?.teams || [],
      guestEmails: guestEmails.filter((e) => e !== email),
    });
  };

  const handleGuestEmailKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddGuestEmail();
    }
  };

  return (
    <Stack spacing={2} data-testid="mhl-auto-route-config-editor">
      {/* Form Templates section - only shown for organisation-level config */}
      {!showGuestEmails && (
        <>
          <Typography variant="subtitle2">Form Templates</Typography>
          <Typography variant="caption" color="textSecondary">
            Select which form templates trigger auto-routing after Part 1
            signature:
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              label="Select All"
              outlined
              small
              disabled={disabled}
              onClick={handleSelectAllFormTemplates}
            />
            <Button
              label="Forms without hospital"
              outlined
              small
              disabled={disabled}
              onClick={handleSelectFormsWithoutHospital}
            />
            <Button
              label="Deselect All"
              outlined
              small
              disabled={disabled}
              onClick={handleClearFormTemplates}
            />
          </Stack>

          <Typeahead<{ id: string; label: string }>
            name="formTemplateSearch"
            placeholder="Search for form templates..."
            disabled={disabled}
            options={availableFormTemplates}
            optionsKey="label"
            resetKey={selectedFormTemplateIds.join("-")}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.label || ""
            }
            inputValue={formTemplateSearchQuery}
            onInputChange={(value: string) => {
              const matchedTemplate = availableFormTemplates.find(
                (t) => t.label === value,
              );
              if (matchedTemplate) {
                handleAddFormTemplate(matchedTemplate.id);
                setTimeout(() => {
                  setFormTemplateSearchQuery("");
                }, 0);
              } else {
                setFormTemplateSearchQuery(value);
              }
            }}
            allowFreeText={false}
            enableFuse={true}
          />

          {selectedFormTemplateIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {selectedFormTemplateIds.map((templateId) => {
                const template = formTemplateOptions.find(
                  (t) => t.id === templateId,
                );
                return (
                  <Chip
                    key={templateId}
                    label={template?.label || templateId}
                    size="small"
                    onDelete={
                      !disabled
                        ? () => {
                            handleRemoveFormTemplate(templateId);
                          }
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
        </>
      )}
      <Typography variant="subtitle2">Teams</Typography>
      <Typography variant="caption" color="textSecondary">
        Search and select teams to automatically receive forms:
      </Typography>
      <Typeahead<{ id: string; name: string; type: string }>
        name="teamSearch"
        placeholder="Search for a team..."
        disabled={disabled}
        loading={loadingTeams}
        options={searchedTeams}
        optionsKey="name"
        resetKey={selectedTeamIds.join("-")}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option?.name
              ? `${option.name} (${option.type || "unknown"})`
              : ""
        }
        inputValue={teamSearchQuery}
        onInputChange={(value: string) => {
          // Check if this matches a team option label (selection made)
          const matchedTeam = searchedTeams.find(
            (team) => `${team.name} (${team.type || "unknown"})` === value,
          );
          if (matchedTeam) {
            handleAddTeam(matchedTeam.id);
            // Delay clearing to avoid flickering from React state updates
            setTimeout(() => {
              setTeamSearchQuery("");
              setSearchedTeams([]);
            }, 0);
          } else {
            setTeamSearchQuery(value);
          }
        }}
        allowFreeText={false}
        enableFuse={false}
      />
      {selectedTeamIds.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {selectedTeamIds.map((teamId) => {
            const details = selectedTeamDetails.get(teamId);
            return (
              <Box
                key={teamId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "action.selected",
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography variant="body2">
                  {details?.name || teamId}
                  {details?.type && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                      sx={{ ml: 0.5 }}
                    >
                      ({details.type})
                    </Typography>
                  )}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    handleRemoveTeam(teamId);
                  }}
                  disabled={disabled}
                  sx={{ ml: 0.5 }}
                >
                  ×
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
      {showGuestEmails && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2">Guest Emails</Typography>
          <Typography variant="caption" color="textSecondary">
            Add email addresses for guest users who should receive routed forms.
            Guest accounts will be created automatically when the configuration
            is saved:
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <MuiTextField
                fullWidth
                size="small"
                placeholder="Enter email address"
                disabled={disabled}
                value={guestEmailInput}
                onChange={(e) => {
                  setGuestEmailInput(e.target.value);
                  setGuestEmailError(null);
                }}
                onKeyDown={handleGuestEmailKeyDown}
                error={!!guestEmailError}
                helperText={guestEmailError}
              />
            </Box>
            <Button
              label="Add"
              small
              disabled={disabled || !guestEmailInput.trim()}
              onClick={handleAddGuestEmail}
            />
          </Stack>

          {guestEmails.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {guestEmails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  color="secondary"
                  onDelete={
                    !disabled
                      ? () => {
                          handleRemoveGuestEmail(email);
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
